import { Logout } from '@mui/icons-material';
import { Avatar, Box, Divider, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import React from 'react';

type HeaderProfileDropdownProps = {
	isAuthenticated: boolean;
	userInfo?: {
		name: string;
		initials: string;
		email: string;
		role: string;
	} | null;
	signOut: () => Promise<void>;
};

export const HeaderProfileDropdown = ({ isAuthenticated, userInfo, signOut }: HeaderProfileDropdownProps) => {
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};
	const handleLogout = async () => {
		await signOut();
		handleClose();
	};

	return isAuthenticated ? (
		<>
			<Tooltip title='Account settings'>
				<IconButton
					onClick={handleClick}
					size='small'
					aria-controls={open ? 'account-menu' : undefined}
					aria-haspopup='true'
					aria-expanded={open}
				>
					<Avatar
						className='header-profile-avatar-icon'
						sx={{
							width: 32,
							height: 32,
							fontSize: 16,
							fontWeight: 'bold',
							bgcolor: 'transparent',
							border: '2px solid currentColor',
						}}
					>
						{userInfo?.initials}
					</Avatar>
				</IconButton>
			</Tooltip>
			<Menu
				anchorEl={anchorEl}
				id='account-menu'
				open={open}
				onClose={handleClose}
				onClick={handleClose}
				slotProps={{
					paper: {
						elevation: 0,
						sx: {
							overflow: 'visible',
							filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
							mt: 0.5,
							p: 1,
							'& .MuiAvatar-root': {
								width: 50,
								height: 50,
								ml: -0.5,
								mr: 1.5,
							},
							'&::before': {
								content: '""',
								display: 'block',
								position: 'absolute',
								top: 0,
								right: 10,
								width: 10,
								height: 10,
								bgcolor: 'background.paper',
								transform: 'translateY(-50%) rotate(45deg)',
								zIndex: 0,
							},
						},
					},
				}}
				transformOrigin={{ horizontal: 'right', vertical: 'top' }}
				anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
			>
				<MenuItem>
					<Avatar className='header-profile-dropdown-avatar-icon'>{userInfo?.initials}</Avatar>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
						<Typography fontWeight='fontWeightBold'>{userInfo?.name}</Typography>
						<Typography variant='body2'>{userInfo?.email}</Typography>
						<Typography variant='body2'>{userInfo?.role}</Typography>
					</Box>
				</MenuItem>
				<Divider />
				<MenuItem onClick={handleLogout} data-cy='sign-out-icon'>
					<ListItemIcon>
						<Logout fontSize='large' sx={{ mr: 1.25 }} />
					</ListItemIcon>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
						<Typography fontWeight='fontWeightBold'>Logout</Typography>
						<Typography variant='body2'>Sign out of ParkChirp</Typography>
					</Box>
				</MenuItem>
			</Menu>
		</>
	) : null;
};
